import {
  type ComponentType,
  Fragment,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import classNames from 'classnames';

import { Button } from '@iconik-io/uad';

import type { ContextMenuContainerProps } from '../../components/context_menu/types';
import HelpButton from '../../components/help_button/help_button';
import { Link } from '../../components/link/link';
import type { ContextMenuWithOptions } from '../../components/trigger_context_menu/trigger_context_menu';
import { toggleLeftNav } from '../../ducks/display/slice';
import Icon, { ICONS } from '../../icons/icon';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import type { ObjectWithId } from '../../types/common';
import GearBoxActions, {
  GearBoxTriggerButton,
} from '../gearbox_actions/gearbox_actions';
import Label from '../label/label';
import type { LabelVariantType } from '../label/label_types';
import type { PageGutterSize } from '../page_content/page_content';

import mixins from '../../styles/core/mixins.module.css';
import styles from './page_header.module.css';

export type Breadcrumb = {
  label: string;
  link: string;
};

export type PageHeaderProps<
  Item extends ObjectWithId,
  State,
  Options,
> = PropsWithChildren<{
  leftMenuButton?: boolean;
  title?: string | ReactElement;
  description?: string;
  helpPath?: string;
  helpTitle?: string;
  contextMenu?:
    | ComponentType<ContextMenuContainerProps<Item, State, Options>>
    | ContextMenuWithOptions<Item, State, Options>;
  isContextMenuDisabled?: boolean;
  contextItems?: Item[];
  breadcrumbs?: Breadcrumb[];
  status?: string;
  statusVariant?: LabelVariantType;
  startActionsBar?: ReactNode;
  endActionsBar?: ReactNode;
  // Left and right padding size, `xs` by default
  gutter?: PageGutterSize;
}>;

export function PageHeader<Item extends ObjectWithId, State, Options>({
  leftMenuButton,
  contextItems = [],
  contextMenu,
  children,
  helpPath,
  helpTitle,
  title,
  description,
  breadcrumbs = [],
  status,
  statusVariant,
  isContextMenuDisabled = false,
  startActionsBar,
  endActionsBar,
  gutter = 'xs',
}: PageHeaderProps<Item, State, Options>) {
  const dispatch = useAppDispatch();
  const leftNav = useAppSelector((state) => state.displayReducer.leftNav);

  const hasFullBar = Boolean(startActionsBar);
  const hasRightBar = Boolean(endActionsBar) && !hasFullBar;

  let actionsRowClass = styles.title_row;
  if (children) {
    actionsRowClass = styles.children_row;
  } else if (description) {
    actionsRowClass = styles.description_row;
  }

  return (
    <div
      className={classNames(styles.page_header, {
        [styles.gutter_m]: gutter === 'm',
      })}
    >
      {title || breadcrumbs?.length ? (
        <div className={styles.title_group}>
          {leftMenuButton ? (
            <Button
              icon={<Icon icon={ICONS.MenuVertical} />}
              variant="tertiary"
              onClick={() => {
                dispatch(toggleLeftNav(!leftNav));
              }}
              className={classNames(
                styles.left_nav_button,
                mixins.reset_button,
              )}
            />
          ) : undefined}
          {breadcrumbs?.length ? (
            <span className={styles.breadcrumbs}>
              {breadcrumbs.map((breadcrumb, index) => (
                <Fragment key={index}>
                  <h1 className={styles.title}>
                    <Link to={breadcrumb.link}>{breadcrumb.label}</Link>
                  </h1>
                  <Icon
                    className={styles.breadcrumb_arrow}
                    icon={ICONS.NavRight}
                    size={15}
                  />
                </Fragment>
              ))}
            </span>
          ) : null}
          {title ? <h1 className={styles.title}>{title}</h1> : null}
        </div>
      ) : null}

      {description && <p className={styles.description}>{description}</p>}

      {children && <div className={styles.children}>{children}</div>}

      <div
        className={classNames(styles.actions_wrapper, {
          [styles.full_bar]: hasFullBar,
          [styles.right_bar]: hasRightBar,
          [actionsRowClass]: !hasFullBar,
        })}
      >
        {status && statusVariant && (
          <Label className={styles.status_indicator} variant={statusVariant}>
            {status}
          </Label>
        )}

        {startActionsBar && (
          <div className={styles.left_actions}>{startActionsBar}</div>
        )}

        {endActionsBar && (
          <div className={styles.right_actions}>{endActionsBar}</div>
        )}

        <div className={styles.context_menu}>
          {contextMenu ? (
            <GearBoxActions
              contextItems={contextItems}
              contextMenu={contextMenu}
              disabled={isContextMenuDisabled}
            />
          ) : null}
          {/* Prevents layout shifts while the context menu is not resolved yet */}
          {!contextMenu && isContextMenuDisabled ? (
            <GearBoxTriggerButton disabled />
          ) : null}
        </div>

        {helpPath ? (
          <div className={styles.help}>
            <HelpButton path={helpPath} title={helpTitle || 'Iconik'} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
